import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import HighDemandProductCard from '../components/HighDemandProductCard';
import HighDemandProductModal from '../components/HighDemandProductModal';
import ConfirmDialog from '../components/ConfirmDialog';

const HighDemandProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, productId: null });

    // Fetch all products
    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/high-demand-products');
            setProducts(response.data.data || []);
        } catch (error) {
            console.error('Error fetching high demand products:', error);
            toast.error('Failed to load high demand products');
        } finally {
            setLoading(false);
        }
    };

    // Fetch settings
    const fetchSettings = async () => {
        try {
            const response = await axios.get('/api/admin/high-demand-products/settings');
            setSettings(response.data.data || {
                section_title: 'High Demand Products',
                section_subtitle: 'We specialize in affordable housing loans for:'
            });
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchSettings();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);

            if (editingProduct) {
                // Update existing product
                await axios.put(`/api/admin/high-demand-products/${editingProduct.id}`, formData);
                toast.success('Product updated successfully');
            } else {
                // Create new product
                await axios.post('/api/admin/high-demand-products', formData);
                toast.success('Product created successfully');
            }

            setIsModalOpen(false);
            setEditingProduct(null);
            fetchProducts();
        } catch (error) {
            console.error('Error saving product:', error);
            toast.error(error.response?.data?.message || 'Failed to save product');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, productId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/high-demand-products/${deleteConfirm.productId}`);
            toast.success('Product deleted successfully');
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Failed to delete product');
        } finally {
            setDeleteConfirm({ isOpen: false, productId: null });
        }
    };

    // Handle edit
    const handleEdit = (product) => {
        setEditingProduct(product);
        setIsModalOpen(true);
    };

    // Handle add new
    const handleAddNew = () => {
        setEditingProduct(null);
        setIsModalOpen(true);
    };

    // Settings handlers
    const handleSettingsChange = (field, value) => {
        setSettings(prev => ({ ...prev, [field]: value }));
    };

    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/high-demand-products/settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">High Demand Products</h1>
                    <p className="text-slate-500 mt-1">Manage loan products for DSA Partner Program</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Product
                </button>
            </div>

            {/* Settings Section */}
            {settings && (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">Section Settings</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Title
                            </label>
                            <input
                                type="text"
                                value={settings.section_title}
                                onChange={(e) => handleSettingsChange('section_title', e.target.value)}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                Section Subtitle
                            </label>
                            <input
                                type="text"
                                value={settings.section_subtitle}
                                onChange={(e) => handleSettingsChange('section_subtitle', e.target.value)}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <div className="mt-4">
                        <button
                            onClick={handleSettingsSave}
                            disabled={savingSettings}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50"
                        >
                            {savingSettings ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            )}

            {/* Products Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                            <div className="h-24 bg-slate-200 rounded-xl mb-4"></div>
                            <div className="h-6 bg-slate-200 rounded mb-3"></div>
                            <div className="h-4 bg-slate-200 rounded mb-2"></div>
                            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                        </div>
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <PlusIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No products yet</h3>
                        <p className="text-slate-500 mb-6">
                            Get started by creating your first high demand product
                        </p>
                        <button
                            onClick={handleAddNew}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all"
                        >
                            Add First Product
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {products.map((product) => (
                        <HighDemandProductCard
                            key={product.id}
                            product={product}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <HighDemandProductModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingProduct(null);
                }}
                onSubmit={handleSubmit}
                product={editingProduct}
                loading={submitting}
            />

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, productId: null })}
                onConfirm={confirmDelete}
                title="Delete Product"
                message="Are you sure you want to delete this high demand product? This action cannot be undone."
            />
        </div>
    );
};

export default HighDemandProductsPage;
